/**
 * PATH: src/pages/pricing/Checkout.jsx
 * PURPOSE: Launch Razorpay checkout for selected plan
 * UI: Material UI
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { createOrder, confirmPayment, validateCoupon } from "./utils/pricingApi";
import { v4 as uuidv4 } from "uuid";

import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";

export default function Checkout() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const [planCode] = useState(state?.planCode || null);
  const [revenueStream] = useState(state?.revenueStream || null);
  const [loading, setLoading] = useState(false);

  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState(null);
  const [couponValid, setCouponValid] = useState(null);

  // ---------------------------
  // Coupon validation
  // ---------------------------

  const handleApplyCoupon = async () => {
    try {
      const res = await validateCoupon(coupon);
      setCouponValid(res.coupon);
      setCouponMsg("Coupon applied successfully");
    } catch (err) {
      setCouponMsg("Invalid or expired coupon");
    }
  };

  // ---------------------------
  // Razorpay Payment Flow
  // ---------------------------

  const handlePay = async () => {
    try {
      setLoading(true);

      const idempotencyKey = uuidv4();

      // 1. Create backend order
      const orderResp = await createOrder(
        {
          planCode,
          revenueStream,
        },
        idempotencyKey
      );

      const { providerOrder, transactionId } = orderResp;

      // 2. Launch Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_xxx",
        amount: providerOrder.amount,
        currency: providerOrder.currency,
        order_id: providerOrder.id,
        name: "AI Innovation Platform",
        description: `Payment for ${planCode}`,
        handler: async function (response) {
          // 3. Confirm payment with backend
          await confirmPayment({
            transactionId,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          navigate("/pricing/success");
        },
        theme: { color: "#1976d2" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      navigate("/pricing/failure");
    }
  };

  if (!planCode) {
    return (
      <Box p={4}>
        <Alert severity="error">Invalid Checkout Session</Alert>
      </Box>
    );
  }

  return (
    <Box p={4} maxWidth="600px" mx="auto">
      <Card elevation={4} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h4" fontWeight={700} mb={2}>
            Checkout
          </Typography>

          <Typography variant="subtitle1" mb={3} color="text.secondary">
            Selected Plan: <strong>{planCode}</strong>
          </Typography>

          {/* Coupon Section */}
          <Box mb={3}>
            <Typography variant="subtitle2" mb={1}>
              Apply Coupon (optional)
            </Typography>

            <Box display="flex" gap={2}>
              <TextField
                label="Coupon Code"
                value={coupon}
                size="small"
                onChange={(e) => setCoupon(e.target.value)}
              />

              <Button
                variant="outlined"
                onClick={handleApplyCoupon}
                disabled={!coupon}
              >
                Apply
              </Button>
            </Box>

            {couponMsg && (
              <Alert
                severity={couponValid ? "success" : "error"}
                sx={{ mt: 2 }}
              >
                {couponMsg}
              </Alert>
            )}
          </Box>

          {/* Pay Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            sx={{ borderRadius: 2, mt: 2 }}
            onClick={handlePay}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Pay"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
